import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { reflectionApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Plus, Eye, Edit, Trash2, Brain, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Reflection } from '@/types/learningManagement';

export const ReflectionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'reflection');
  const canEdit = hasPermission('edit', 'reflection');
  const canDelete = hasPermission('delete', 'reflection');

  // Build fetch function with search support
  const fetchReflectionsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return reflectionApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else {
        return reflectionApi.list({
          page: params.page,
          size: params.size,
          sortBy: 'createdAt',
          sortDirection: 'desc',
        });
      }
    };
  }, [searchQuery]);

  // Use paginated list hook
  const {
    data: reflections,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Reflection>({
    fetchFn: fetchReflectionsFn,
    entityName: 'reflections',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (reflectionId: string) => {
      await reflectionApi.delete(reflectionId);
    },
    entityName: 'reflection',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (reflectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this reflection?')) return;
    await deleteMutation.mutate(reflectionId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const reflectionId = (reflection: Reflection) => reflection.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reflection Management</h1>
          <p className="text-muted-foreground mt-2">Manage all reflections</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/reflections/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Reflection
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Search reflections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reflections Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reflections ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all reflections</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading reflections..." />
          ) : (error || reflections.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load reflections. Please try again or create a new one." : "Get started by creating your first reflection."}
              icon={Brain}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/reflections/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Skill ID</TableHead>
                    <TableHead>Time Invested (Hours)</TableHead>
                    <TableHead>Skill Level Improvement</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reflections.map((reflection) => (
                    <TableRow key={reflectionId(reflection)}>
                      <TableCell className="font-medium">{reflection.skillId || 'N/A'}</TableCell>
                      <TableCell>{reflection.timeInvestedHours || 'N/A'}</TableCell>
                      <TableCell>{reflection.skillLevelImprovement || 'N/A'}</TableCell>
                      <TableCell>
                        {formatDate(reflection.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/reflections/${reflectionId(reflection)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/reflections/${reflectionId(reflection)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(reflectionId(reflection))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReflectionListPage;






